/**
 * Public /help index. Hand-coded card grid that mirrors the docs/help/
 * folder layout. Each card links to /help/<slug>, which renders the
 * markdown guides for that category in-app via Vite ?raw imports
 * (see web/routes/help.$slug.tsx).
 *
 * Earlier iteration linked cards to the GitHub repo's docs/help/<slug>
 * folder; that was a stop-gap that 404'd in production because the
 * Manos repo is private. Replaced 2026-05-08 with the in-app renderer.
 */

import { Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Compass,
  CurrencyGbp,
  Wallet,
  CalendarBlank,
  UsersThree,
  Briefcase,
  CreditCard,
  Wrench,
  CaretRight,
} from "@phosphor-icons/react";

interface HelpCategory {
  slug: string;
  title: string;
  description: string;
  icon: typeof Compass;
}

const CATEGORIES: HelpCategory[] = [
  {
    slug: "getting-started",
    title: "Getting started",
    description: "Sign up, your trial, and the first 30 minutes.",
    icon: Compass,
  },
  {
    slug: "money-in",
    title: "Money in",
    description: "Quotes, invoices, customer payments, BNPL, accountant export.",
    icon: CurrencyGbp,
  },
  {
    slug: "money-out",
    title: "Money out",
    description: "Expenses and recurring costs.",
    icon: Wallet,
  },
  {
    slug: "day-to-day",
    title: "Day to day",
    description: "Inbox, schedule, jobs, and capturing on the go.",
    icon: CalendarBlank,
  },
  {
    slug: "people",
    title: "People",
    description: "Clients, contacts, sub-contractors, your team.",
    icon: UsersThree,
  },
  {
    slug: "your-business",
    title: "Your business",
    description: "Profile, tax, public directory, accounting and calendar links.",
    icon: Briefcase,
  },
  {
    slug: "subscription",
    title: "Subscription",
    description: "Your plan, switching plan, cancelling.",
    icon: CreditCard,
  },
  {
    slug: "troubleshooting",
    title: "Troubleshooting",
    description: "Common errors and what to do when something looks wrong.",
    icon: Wrench,
  },
];

export default function HelpIndex() {
  return (
    <div className="min-h-screen bg-background px-6 py-12">
      <div className="max-w-5xl mx-auto space-y-10">
        <header className="space-y-3">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; Back to Manos
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">Help</h1>
          <p className="text-base text-muted-foreground max-w-2xl">
            Pick a topic. Each guide is short and walks you through one
            thing. If you want a tour of the page you're on, press the
            "?" icon at the top of any page.
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <Link
                key={cat.slug}
                to={`/help/${cat.slug}`}
                className="block group"
              >
                <Card className="h-full transition-colors group-hover:border-amber-500/60 group-hover:bg-accent/40">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <Icon
                        className="h-6 w-6 text-amber-600"
                        weight="duotone"
                      />
                      <CaretRight
                        className="h-4 w-4 text-muted-foreground group-hover:text-foreground"
                        weight="bold"
                      />
                    </div>
                    <CardTitle className="text-base">{cat.title}</CardTitle>
                    <CardDescription>{cat.description}</CardDescription>
                  </CardHeader>
                  <CardContent />
                </Card>
              </Link>
            );
          })}
        </div>

        <div className="pt-6 border-t border-border">
          <Card className="bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-700/40">
            <CardHeader>
              <CardTitle className="text-base">
                Can't find what you need?
              </CardTitle>
              <CardDescription>
                Send us an inquiry and we'll get back to you. You can read
                the reply (and follow up) from your Help &amp; Support hub.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link
                to="/help/messages/new"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold"
              >
                Send a support inquiry
                <CaretRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
}
